// src/pages/Public/InscriptionsEventsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  getAllEvents,
  registerLicencieToEvent,
  unregisterLicencieFromEvent,
  getEventsForLicencie, // ← utilise ton service qui renvoie un Array<Event>
} from "../../services/eventServices";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaCalendarCheck,
  FaTimes, // pour désinscrire dans le tableau
} from "react-icons/fa";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../components/Modal/Modal";

/* ========= Helpers ========= */

// Saison judo (septembre → août)
function computeCurrentSeason(d = new Date()) {
  const y = d.getFullYear();
  const m = d.getMonth(); // 0..11
  return m >= 8 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
}

function normalize(s) {
  return (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

function isCoursCentreNational(type_event) {
  return normalize(type_event) === normalize("Cours Centre National");
}

function startOfISOWeek(d) {
  const date = new Date(d);
  const day = date.getDay(); // 0 = Dim, 1 = Lun, ...
  const diff = (day === 0 ? -6 : 1) - day; // ramène à lundi
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}
function endOfISOWeek(d) {
  const start = startOfISOWeek(d);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

// chevauchement avec la semaine ?
function eventOverlapsWeek(ev, wStart, wEnd) {
  const s = ev?.start ? new Date(ev.start) : null;
  if (!s) return false;
  const e = ev?.end ? new Date(ev.end) : s;
  return e >= wStart && s <= wEnd;
}

// Styles par jour
const DAY_STYLES = {
  lundi: "bg-indigo-600 text-white",
  mardi: "bg-violet-600 text-white",
  mercredi: "bg-emerald-600 text-white",
  jeudi: "bg-amber-500 text-white",
  vendredi: "bg-sky-600 text-white",
  samedi: "bg-rose-600 text-white",
  dimanche: "bg-slate-700 text-white",
};

function DayTag({ date }) {
  const d = date instanceof Date ? date : new Date(date);
  let w = new Intl.DateTimeFormat("fr-FR", { weekday: "long" }).format(d); // ex: "lundi"
  const key = w.toLowerCase();
  const style = DAY_STYLES[key] || "bg-slate-600 text-white";
  w = w.charAt(0).toUpperCase() + w.slice(1);
  return (
    <span
      className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-bold tracking-wide ring-1 ring-black/5 ${style}`}
      aria-label={`Jour : ${w}`}
      title={w}
    >
      {w}
    </span>
  );
}

const LS_FORM_KEY = "na_public_licencie_profile_v1";
const LS_LAST_EVENT = "na_public_last_event_id";

/* ========= Page ========= */

export default function InscriptionsEventsPage() {
  // Events
  const [eventsThisWeek, setEventsThisWeek] = useState([]);
  const [eventsNextWeek, setEventsNextWeek] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  // Semaine courante
  const today = new Date();
  const weekStart = useMemo(() => startOfISOWeek(today), []);
  const weekEnd = useMemo(() => endOfISOWeek(today), []);

  // Semaine prochaine (lundi→dimanche)
  const nextWeekStart = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    return d;
  }, [weekStart]);

  const nextWeekEnd = useMemo(() => endOfISOWeek(nextWeekStart), [nextWeekStart]);

  // Sélection Event
  const [selectedEventId, setSelectedEventId] = useState(null);

  // Profil Licencié (non connecté)
  const [form, setForm] = useState({
    nom: "",
    prenom: "",
    club: "",
    genre: "M", // "M" | "F"
    licence_number: "",
    saison: computeCurrentSeason(),
    remember: true,
  });
  const [hasSavedProfile, setHasSavedProfile] = useState(false);

  // Licencié résolu et events où il est inscrit
  const [licencieId, setLicencieId] = useState(null);
  const [registeredEventIds, setRegisteredEventIds] = useState(new Set());
  const [registeredEvents, setRegisteredEvents] = useState([]); // pour le tableau récap

  // UI / état
  const [isFormOpen, setIsFormOpen] = useState(false); // MODALE de saisie
  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [status, setStatus] = useState(null); // {type:"success"|"error"|"info", msg:string}

  // Charger les events (actifs + CCN + cette semaine + semaine prochaine)
  useEffect(() => {
    (async () => {
      setLoadingEvents(true);
      try {
        const data = await getAllEvents();
        const list = Array.isArray(data) ? data.filter((e) => e.is_active) : [];

        const base = list.filter((e) => isCoursCentreNational(e.type_event));

        const weekly = base
          .filter((e) => eventOverlapsWeek(e, weekStart, weekEnd))
          .sort((a, b) => new Date(a.start) - new Date(b.start));

        const nextWeekly = base
          .filter((e) => eventOverlapsWeek(e, nextWeekStart, nextWeekEnd))
          .sort((a, b) => new Date(a.start) - new Date(b.start));

        setEventsThisWeek(weekly);
        setEventsNextWeek(nextWeekly);

        // Déterminer l'event par défaut
        const fromLS = Number(localStorage.getItem(LS_LAST_EVENT) || 0);
        const all = [...weekly, ...nextWeekly];

        let def = all.find((e) => e.id === fromLS);

        if (!def) {
          // Event "d'aujourd'hui" (sur l'une des deux semaines)
          const isToday = (ev) => {
            const s = new Date(ev.start);
            const e = ev.end ? new Date(ev.end) : s;
            const d = new Date();
            d.setHours(12, 0, 0, 0);
            const d0 = new Date(s.getFullYear(), s.getMonth(), s.getDate());
            const d1 = new Date(e.getFullYear(), e.getMonth(), e.getDate());
            return d >= d0 && d <= d1;
          };
          def = weekly.find(isToday) || nextWeekly.find(isToday);
        }

        if (!def && all.length) def = all[0];

        setSelectedEventId(def?.id ?? null);
      } finally {
        setLoadingEvents(false);
      }
    })();
  }, [weekStart, weekEnd, nextWeekStart, nextWeekEnd]);

  // Charger profil depuis localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_FORM_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        setForm((s) => ({
          ...s,
          ...saved,
          saison: computeCurrentSeason(), // suggestion saison actuelle
          remember: true,
        }));
        setHasSavedProfile(true);
      }
    } catch {
      // ignore
    }
  }, []);

  // Sauvegarder dernier event choisi
  useEffect(() => {
    if (selectedEventId)
      localStorage.setItem(LS_LAST_EVENT, String(selectedEventId));
  }, [selectedEventId]);

  // Résoudre licencieId à partir du couple (licence_number, saison)
  const resolveLicencieId = async () => {
    const licence_number = String(form.licence_number || "")
      .trim()
      .toUpperCase();
    const saison = String(form.saison || "").trim();
    if (!licence_number || !saison) return null;

    const base = process.env.REACT_APP_API_URL;
    const url = `${base}/api/licencies/check?licence_number=${encodeURIComponent(
      licence_number
    )}&saison=${encodeURIComponent(saison)}`;

    const resp = await fetch(url);
    if (!resp.ok) throw new Error("Vérification licencié impossible.");
    const data = await resp.json();
    return data?.found ? data?.licencie?.id ?? null : null;
  };

  // Recharger les events inscrits pour le licencié (sur les 2 semaines affichées)
  const refreshRegisteredEvents = async (forceLicencieId = null) => {
    try {
      const lid = forceLicencieId ?? licencieId ?? (await resolveLicencieId());
      if (!lid) {
        setLicencieId(null);
        setRegisteredEventIds(new Set());
        setRegisteredEvents([]);
        return;
      }
      setLicencieId(lid);

      // bornes : du lundi de cette semaine → dimanche de la semaine prochaine
      const fromISO = new Date(weekStart).toISOString();
      const toISO = new Date(nextWeekEnd).toISOString();

      // getEventsForLicencie renvoie un Array<Event>
      const arr = await getEventsForLicencie(lid, { from: fromISO, to: toISO });

      setRegisteredEvents(Array.isArray(arr) ? arr : []);
      setRegisteredEventIds(
        new Set((Array.isArray(arr) ? arr : []).map((ev) => Number(ev.id)))
      );
    } catch (e) {
      console.warn("Impossible de récupérer les inscriptions du licencié:", e);
      setRegisteredEventIds(new Set());
      setRegisteredEvents([]);
    }
  };

  // Recalculer les inscriptions lorsque :
  // - les événements changent (chargement terminé)
  // - le profil (licence/saison) change
  useEffect(() => {
    if (loadingEvents) return;
    refreshRegisteredEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loadingEvents,
    form.licence_number,
    form.saison,
    weekStart,
    weekEnd,
    nextWeekStart,
    nextWeekEnd,
  ]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };

  const resetMessages = () => setStatus(null);

  const clearSavedProfile = () => {
    localStorage.removeItem(LS_FORM_KEY);
    setHasSavedProfile(false);
    setStatus({ type: "info", msg: "Profil local supprimé sur cet appareil." });
    setRegisteredEventIds(new Set());
    setRegisteredEvents([]);
    setLicencieId(null);
  };

  const selectedEvent = useMemo(() => {
    const all = [...eventsThisWeek, ...eventsNextWeek];
    return all.find((e) => e.id === Number(selectedEventId)) || null;
  }, [eventsThisWeek, eventsNextWeek, selectedEventId]);

  const unregister = async () => {
    resetMessages();

    if (!selectedEventId) {
      setStatus({ type: "error", msg: "Veuillez sélectionner un événement." });
      return;
    }

    const licence_number = String(form.licence_number || "")
      .trim()
      .toUpperCase();
    const saison = String(form.saison || "").trim();
    if (!licence_number || !saison) {
      setStatus({
        type: "error",
        msg:
          "Renseignez d’abord un profil (numéro de licence + saison) pour supprimer la présence.",
      });
      return;
    }

    try {
      setRemoving(true);

      // 1) Récupérer le licencie_id
      const lid = await resolveLicencieId();
      if (!lid) {
        setStatus({
          type: "error",
          msg: "Licencié introuvable pour cette saison.",
        });
        return;
      }

      // 2) Désinscrire du cours sélectionné
      await unregisterLicencieFromEvent(Number(selectedEventId), Number(lid));

      // 3) Rafraîchir l'état "inscrit"
      await refreshRegisteredEvents(lid);

      setStatus({ type: "success", msg: "Présence supprimée pour ce cours." });
    } catch (err) {
      const apiMsg =
        err?.response?.data?.error ||
        err?.message ||
        "Impossible de supprimer la présence.";
      setStatus({ type: "error", msg: apiMsg });
    } finally {
      setRemoving(false);
    }
  };

  // Désinscription depuis la table récap
  const unregisterFromRow = async (eventId) => {
    if (!eventId) return;
    try {
      setRemoving(true);
      const lid = licencieId ?? (await resolveLicencieId());
      if (!lid) return;

      await unregisterLicencieFromEvent(Number(eventId), Number(lid));
      await refreshRegisteredEvents(lid);
      setStatus({ type: "success", msg: "Présence supprimée." });
    } catch (err) {
      const apiMsg =
        err?.response?.data?.error ||
        err?.message ||
        "Suppression impossible.";
      setStatus({ type: "error", msg: apiMsg });
    } finally {
      setRemoving(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    resetMessages();

    if (!selectedEventId) {
      setStatus({ type: "error", msg: "Veuillez sélectionner un cours." });
      return;
    }

    // Validations
    const required = [
      "nom",
      "prenom",
      "club",
      "genre",
      "licence_number",
      "saison",
    ];
    for (const f of required) {
      if (!String(form[f] || "").trim()) {
        setStatus({ type: "error", msg: `Champ requis manquant : ${f}.` });
        return;
      }
    }
    if (!/^\d{4}-\d{4}$/.test(String(form.saison))) {
      setStatus({
        type: "error",
        msg: "La saison doit être au format 'YYYY-YYYY'.",
      });
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        nom: String(form.nom).trim(),
        prenom: String(form.prenom).trim(),
        club: String(form.club).trim(),
        genre: String(form.genre || "M"),
        licence_number: String(form.licence_number).trim().toUpperCase(),
        saison: String(form.saison).trim(),
      };

      await registerLicencieToEvent(Number(selectedEventId), payload);

      // Mémoriser profil si demandé
      if (form.remember) {
        localStorage.setItem(
          LS_FORM_KEY,
          JSON.stringify({
            nom: payload.nom,
            prenom: payload.prenom,
            club: payload.club,
            genre: payload.genre,
            licence_number: payload.licence_number,
          })
        );
        setHasSavedProfile(true);
      }

      // Après succès, rafraîchir la liste d'événements "inscrits"
      const lid = await resolveLicencieId();
      await refreshRegisteredEvents(lid);

      // Fermer immédiatement la modale après succès
      setStatus(null);
      setIsFormOpen(false);
    } catch (err) {
      const apiMsg =
        err?.response?.data?.error ||
        "Impossible d’enregistrer la présence pour le moment.";
      setStatus({ type: "error", msg: apiMsg });
    } finally {
      setSubmitting(false);
    }
  };

  /* ====== UI fragments ====== */

  const EventList = ({
    title,
    events,
    weekStart,
    weekEnd,
    selectedEventId,
    onSelect,
    loading,
  }) => (
    <div className="rounded-2xl border bg-white dark:bg-neutral-800 shadow">
      <div className="p-4 border-b flex items-center gap-2">
        <FaCalendarAlt />
        <h2 className="font-semibold">{title}</h2>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="text-sm opacity-70">Chargement des cours…</div>
        ) : events.length === 0 ? (
          <div className="text-sm opacity-70">Aucun cours sur cette période.</div>
        ) : (
          <ul className="divide-y rounded-lg border">
            {events.map((ev) => {
              const s = new Date(ev.start);
              const e = ev.end ? new Date(ev.end) : null;
              const dateLabel = e
                ? `${s.toLocaleDateString("fr-FR")} → ${e.toLocaleDateString(
                    "fr-FR"
                  )}`
                : s.toLocaleDateString("fr-FR");
              const isSelected = Number(selectedEventId) === Number(ev.id);
              const isRegistered = registeredEventIds.has(Number(ev.id));

              return (
                <li
                  key={ev.id}
                  onClick={() => onSelect(ev.id)}
                  className={[
                    "p-3 cursor-pointer transition flex items-start gap-3",
                    isRegistered
                      ? "bg-green-50/60 dark:bg-green-900/20"
                      : isSelected
                      ? "bg-slate-50 dark:bg-neutral-700/40"
                      : "hover:bg-slate-50 dark:hover:bg-neutral-700",
                    isRegistered ? "border-l-4 border-green-500" : "",
                  ].join(" ")}
                  title={ev.titre}
                >
                  {/* Tag du jour */}
                  <DayTag date={s} />

                  <div className="flex-1">
                    <div className="font-medium line-clamp-1">{ev.titre}</div>
                    <div className="text-xs opacity-80">
                      {dateLabel} — {ev.lieu_event || ev.organisateur || "—"}
                    </div>
                    {ev.description && (
                      <div className="text-xs opacity-80">
                        <span className="font-bold">Description :</span>{" "}
                        {ev.description}
                      </div>
                    )}
                    {ev.horaire_event && (
                      <div className="text-xs opacity-80">
                        <span className="font-bold">Horaires :</span>{" "}
                        {ev.horaire_event}
                      </div>
                    )}
                  </div>

                  {isRegistered ? (
                    <FaCheckCircle className="text-green-600 dark:text-green-400 text-2xl mt-1 shrink-0" />
                  ) : isSelected ? (
                    <FaCalendarCheck className="text-slate-500 dark:text-slate-300 text-2xl mt-1 shrink-0" />
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-3 text-xs opacity-80">
          Semaine du{" "}
          <span className="font-medium">
            {weekStart.toLocaleDateString("fr-FR")}
          </span>{" "}
          au{" "}
          <span className="font-medium">
            {weekEnd.toLocaleDateString("fr-FR")}
          </span>
        </div>
      </div>
    </div>
  );

  const ProfileSummary = () => (
    <div className="rounded-2xl border bg-white dark:bg-neutral-800 shadow">
      <div className="p-4 border-b">
        <h2 className="font-semibold">Licencié</h2>
        <p className="text-sm opacity-80">
          Saisissez ou modifiez les informations du licencié dans la fenêtre.
        </p>
      </div>

      <div className="p-4 grid gap-2 text-sm">
        {hasSavedProfile ? (
          <>
            <div>
              <span className="font-medium">Nom :</span> {form.nom}
            </div>
            <div>
              <span className="font-medium">Prénom :</span> {form.prenom}
            </div>
            <div>
              <span className="font-medium">Club :</span> {form.club}
            </div>
            <div>
              <span className="font-medium">Genre :</span> {form.genre}
            </div>
            <div>
              <span className="font-medium">N° licence :</span>{" "}
              {form.licence_number}
            </div>
          </>
        ) : (
          <div className="opacity-80">Aucun profil enregistré sur cet appareil.</div>
        )}
      </div>

      {status && (
        <div
          className={`mx-4 mb-3 rounded-lg px-3 py-2 text-sm ${
            status.type === "success"
              ? "bg-green-50 text-green-800 border border-green-200"
              : status.type === "error"
              ? "bg-red-50 text-red-800 border border-red-200"
              : "bg-yellow-50 text-yellow-800 border border-yellow-200"
          }`}
        >
          <div className="flex items-center gap-2">
            {status.type === "success" ? (
              <FaCheckCircle />
            ) : status.type === "error" ? (
              <FaExclamationTriangle />
            ) : (
              <FaInfoCircle />
            )}
            <span>{status.msg}</span>
          </div>
        </div>
      )}

      <div className="p-4 pt-0 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => {
            setStatus(null);
            setIsFormOpen(true);
          }}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
        >
          {hasSavedProfile
            ? "Modifier / Valider l’inscription"
            : "Saisir mes informations"}
        </button>

        <button
          type="button"
          onClick={clearSavedProfile}
          className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-neutral-700"
        >
          Supprimer le profil local
        </button>

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()} // ne vole JAMAIS le focus
          onPointerDown={(e) => e.preventDefault()}
          onClick={unregister}
          disabled={removing || !selectedEventId}
          className="ml-auto px-4 py-2 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-60"
        >
          {removing ? "Suppression…" : "Supprimer la présence"}
        </button>
      </div>

      {/* ===== Tableau des événements où le licencié est inscrit ===== */}
      <RegisteredEvents />
    </div>
  );

  // Tableau récap des inscriptions du licencié (période affichée)
  const RegisteredEvents = () => {
  const rows = registeredEvents
    .slice()
    .sort((a, b) => new Date(a.start) - new Date(b.start));

  return (
    <div className="px-4 pb-4">
      <h3 className="mt-2 mb-2 font-semibold text-sm">
        Inscriptions sur la période (semaine en cours + suivante)
      </h3>

      {rows.length === 0 ? (
        <div className="text-sm opacity-70">
          Aucune inscription trouvée sur cette période.
        </div>
      ) : (
        <>
          {/* ===== Vue cartes (mobile) ===== */}
          <div className="md:hidden space-y-3">
            {rows.map((ev) => {
              const s = new Date(ev.start);
              return (
                <div
                  key={ev.id}
                  className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 bg-white dark:bg-neutral-800 p-3 flex gap-3"
                >
                  <div className="pt-0.5">
                    <DayTag date={s} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {s.toLocaleDateString("fr-FR")}
                    </div>
                    <div className="font-medium truncate">{ev.titre}</div>
                    <div className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">
                      {ev.lieu_event || ev.organisateur || "—"}
                    </div>
                    {ev.horaire_event && (
                      <div className="text-xs text-slate-600 dark:text-slate-300">
                        {ev.horaire_event}
                      </div>
                    )}
                  </div>

                  <div className="shrink-0 flex items-center">
                    <button
                      type="button"
                      onClick={() => unregisterFromRow(ev.id)}
                      disabled={removing}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-60"
                      title="Se désinscrire de ce cours"
                    >
                      <FaTimes />
                      <span className="whitespace-nowrap">Se désinscrire</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ===== Vue tableau (≥ md) ===== */}
          <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
            <table className="min-w-[720px] w-full text-sm">
              <thead className="bg-slate-50 dark:bg-neutral-900/40 dark:text-gray-300">
                <tr className="text-left">
                  <th className="px-3 py-2 dark:text-gray-300">Jour</th>
                  <th className="px-3 py-2 dark:text-gray-300">Date</th>
                  <th className="px-3 py-2 dark:text-gray-300">Titre</th>
                  <th className="px-3 py-2 dark:text-gray-300">Lieu</th>
                  <th className="px-3 py-2 dark:text-gray-300">Horaires</th>
                  <th className="px-3 py-2 text-right dark:text-gray-300">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((ev) => {
                  const s = new Date(ev.start);
                  return (
                    <tr
                      key={ev.id}
                      className="border-t border-slate-200 dark:border-slate-700 hover:bg-slate-50/70 dark:hover:bg-neutral-700/40"
                    >
                      <td className="px-3 py-2">
                        <DayTag date={s} />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {s.toLocaleDateString("fr-FR")}
                      </td>
                      <td className="px-3 py-2">{ev.titre}</td>
                      <td className="px-3 py-2">
                        {ev.lieu_event || ev.organisateur || "—"}
                      </td>
                      <td className="px-3 py-2">{ev.horaire_event || "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => unregisterFromRow(ev.id)}
                          disabled={removing}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-60"
                          title="Se désinscrire de ce cours"
                        >
                          <FaTimes />
                          <span>Se désinscrire</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};


  /* ====== Render ====== */
  return (
    <div className="w-full pt-[90px]">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 text-white py-8 shadow">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
            Inscriptions Cours Centre National (semaine en cours)
          </h1>
          <p className="opacity-90 mt-1">
            Choisissez votre cours de la semaine puis enregistrez la présence du
            licencié.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6 grid gap-6 lg:grid-cols-3">
        {/* Col gauche : listes des cours */}
        <section className="lg:col-span-1 space-y-6">
          <EventList
            title="Cours de la semaine (Centre National)"
            events={eventsThisWeek}
            weekStart={weekStart}
            weekEnd={weekEnd}
            selectedEventId={selectedEventId}
            onSelect={setSelectedEventId}
            loading={loadingEvents}
          />

          <EventList
            title="Cours de la semaine prochaine (Centre National)"
            events={eventsNextWeek}
            weekStart={nextWeekStart}
            weekEnd={nextWeekEnd}
            selectedEventId={selectedEventId}
            onSelect={setSelectedEventId}
            loading={loadingEvents}
          />
        </section>

        {/* Col droite : résumé + actions */}
        <section className="lg:col-span-2">
          <ProfileSummary />
        </section>
      </div>

      {/* ===== Modal de saisie du licencié ===== */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        size="md"
        labelledById="licencie-form-title"
        describedById="licencie-form-desc"
        initialFocusSelector="#input-nom"
      >
        <form onSubmit={submit} autoComplete="on" className="flex flex-col min-h-0">
          <ModalHeader
            id="licencie-form-title"
            title="Informations licencié"
            subtitle="Ces données ne créent pas de compte ; elles permettent d'enregistrer la présence sur le cours."
            onClose={() => setIsFormOpen(false)}
          />
          <ModalBody id="licencie-form-desc">
            {status && (
              <div
                className={`mb-4 rounded-lg px-3 py-2 text-sm ${
                  status.type === "success"
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : status.type === "error"
                    ? "bg-red-50 text-red-800 border border-red-200"
                    : "bg-yellow-50 text-yellow-800 border border-yellow-200"
                }`}
              >
                <div className="flex items-center gap-2">
                  {status.type === "success" ? (
                    <FaCheckCircle />
                  ) : status.type === "error" ? (
                    <FaExclamationTriangle />
                  ) : (
                    <FaInfoCircle />
                  )}
                  <span>{status.msg}</span>
                </div>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-xs uppercase tracking-wide opacity-70 dark:text-gray-300">
                  Nom
                </label>
                <input
                  id="input-nom"
                  data-autofocus
                  type="text"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-gray-200"
                  name="nom"
                  value={form.nom}
                  onChange={onChange}
                  required
                  autoComplete="family-name"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide opacity-70 dark:text-gray-300">
                  Prénom
                </label>
                <input
                  type="text"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-gray-200"
                  name="prenom"
                  value={form.prenom}
                  onChange={onChange}
                  required
                  autoComplete="given-name"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-wide opacity-70 dark:text-gray-300">
                  Club
                </label>
                <input
                  type="text"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-gray-200"
                  name="club"
                  value={form.club}
                  onChange={onChange}
                  required
                  autoComplete="organization"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-wide opacity-70 dark:text-gray-300">
                  Genre
                </label>
                <select
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2  bg-gray-200"
                  name="genre"
                  value={form.genre}
                  onChange={onChange}
                  required
                >
                  <option value="M">M</option>
                  <option value="F">F</option>
                </select>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wide opacity-70 dark:text-gray-300">
                  Numéro de licence
                </label>
                <input
                  type="text"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-gray-200"
                  name="licence_number"
                  value={form.licence_number}
                  onChange={onChange}
                  placeholder="Ex: M10071971VICTO01"
                  required
                  autoComplete="off"
                  inputMode="text"
                />
              </div>

              {/* <div>
                <label className="text-xs uppercase tracking-wide opacity-70">
                  Saison
                </label>
                <input
                  type="text"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  name="saison"
                  value={form.saison}
                  onChange={onChange}
                  placeholder="YYYY-YYYY"
                  required
                  autoComplete="off"
                  inputMode="text"
                />
                <div className="text-xs opacity-70 mt-1">
                  Saison suggérée : {computeCurrentSeason()}
                </div>
              </div> */}

              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300"
                    name="remember"
                    checked={form.remember}
                    onChange={onChange}
                  />
                  <span className="dark:text-gray-300">Se souvenir de ces informations sur cet appareil</span>
                </label>
              </div>
            </div>
          </ModalBody>

          <ModalFooter>
            <button
              type="button"
              onClick={() => setIsFormOpen(false)}
              className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50"
            >
              Fermer
            </button>
            <button
              type="submit"
              disabled={submitting || !selectedEventId}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {submitting ? "Enregistrement…" : "Valider l’inscription"}
            </button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
}
